import React, { useState, useEffect, useRef } from 'react';

const InventoryFilters = ({
  onFilter,
  categories = [],
  loading = false,
  totalProducts = 0
}) => {
  const [filters, setFilters] = useState({
    search: '',
    category: ''
  });

  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  const lastSearchRef = useRef('');

  // Debounced search - 500ms
  useEffect(() => {
    if (lastSearchRef.current === filters.search) return;
    if (isInitialMount.current && filters.search === '') return;

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    lastSearchRef.current = filters.search;

    searchTimeoutRef.current = setTimeout(() => {
      onFilter(filters);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [filters.search, onFilter]);

  // Natychmiastowy filtr dla zmian kategorii
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onFilter(filters);
  }, [filters.category]);

  const handleSearchChange = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
  };

  const handleCategoryChange = (e) => {
    setFilters(prev => ({ ...prev, category: e.target.value }));
  };

  const hasActiveFilters = filters.search || filters.category;

  return (
    <div style={{
      width: '100%',
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '4px',
      padding: '0.5rem 0.75rem',
      marginBottom: '0.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      fontSize: '11px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.5rem'
      }}>
        <span style={{
          margin: 0,
          color: 'var(--text-secondary)',
          fontWeight: '600',
          fontSize: '11px'
        }}>
          <i className="fas fa-filter me-1" style={{ color: 'var(--text-muted)', fontSize: '10px' }}></i>
          Filtry i wyszukiwanie
        </span>
        <div style={{
          fontSize: '10px',
          color: 'var(--text-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem'
        }}>
          <i className="fas fa-boxes" style={{ fontSize: '9px' }}></i>
          <span>Znaleziono: <strong>{totalProducts}</strong></span>
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'end',
        flexWrap: 'wrap'
      }}>
        {/* Wyszukiwanie */}
        <div style={{ flex: '0 0 45%', minWidth: '200px' }}>
          <label style={{
            fontSize: '11px', fontWeight: '600',
            color: 'var(--text-muted)', display: 'block', marginBottom: '0.15rem'
          }}>
            <i className="fas fa-search me-1" style={{ color: 'var(--info)', fontSize: '9px' }}></i>
            Wyszukaj produkty
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Nazwa, opis, kod kreskowy..."
              value={filters.search}
              onChange={handleSearchChange}
              disabled={loading}
              style={{
                width: '100%', padding: '6px 10px', paddingLeft: '1.5rem',
                fontSize: '12px', border: '1px solid var(--border)',
                borderRadius: '6px', backgroundColor: 'var(--card-bg)',
                color: 'var(--text)', boxSizing: 'border-box'
              }}
            />
            <i className="fas fa-search" style={{
              position: 'absolute', left: '0.4rem', top: '50%',
              transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '10px'
            }}></i>
            {filters.search && (
              <button
                type="button"
                onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                style={{
                  position: 'absolute', right: '0.4rem', top: '50%',
                  transform: 'translateY(-50%)', background: 'none',
                  color: 'var(--text-muted)', cursor: 'pointer', fontSize: '10px', padding: '0'
                }}
                title="Wyczyść wyszukiwanie"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        {/* Kategoria */}
        <div style={{ flex: '0 0 25%', minWidth: '120px' }}>
          <label style={{
            fontSize: '11px', fontWeight: '600',
            color: 'var(--text-muted)', display: 'block', marginBottom: '0.15rem'
          }}>
            <i className="fas fa-tags me-1" style={{ color: 'var(--warning)', fontSize: '9px' }}></i>
            Kategoria
          </label>
          <select
            value={filters.category}
            onChange={handleCategoryChange}
            disabled={loading}
            style={{
              width: '100%', padding: '6px 10px', fontSize: '12px',
              border: '1px solid var(--border)', borderRadius: '6px',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)', boxSizing: 'border-box'
            }}
          >
            <option value="">Wszystkie</option>
            {categories.map((category, index) => (
              <option key={index} value={category.name}>
                {category.name} ({category.product_count || 0})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Aktywne filtry */}
      {hasActiveFilters && (
        <div style={{
          marginTop: '0.4rem', paddingTop: '0.4rem',
          borderTop: '1px solid var(--border)'
        }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '0.3rem', alignItems: 'center'
          }}>
            <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '600' }}>
              Aktywne filtry:
            </span>

            {filters.search && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.15rem',
                padding: '0.15rem 0.35rem', fontSize: '9px',
                border: '1px solid var(--info)', color: 'var(--info)', borderRadius: '3px'
              }}>
                Szukaj: "{filters.search}"
                <button type="button"
                  onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                  style={{ background: 'none', color: 'var(--info)', cursor: 'pointer', fontSize: '8px', marginLeft: '0.15rem', padding: '0' }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </span>
            )}

            {filters.category && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.15rem',
                padding: '0.15rem 0.35rem', fontSize: '9px',
                backgroundColor: 'var(--warning)', color: 'var(--text)', borderRadius: '3px'
              }}>
                Kategoria: {filters.category}
                <button type="button"
                  onClick={() => setFilters(prev => ({ ...prev, category: '' }))}
                  style={{ background: 'none', color: 'var(--text)', cursor: 'pointer', fontSize: '8px', marginLeft: '0.15rem', padding: '0' }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryFilters;
